import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import {
  LogistikCart,
  LogistikCartItem,
  LogistikLearningMaterialCartItem,
  LogistikWorkshoppackCartiItem,
} from '@chancenwerk/db/LogistikCart.js';
import {
  LogistikLearningMaterialOrderItem,
  LogistikOrderChancenBriefItem,
  LogistikOrderClearanceState,
  LogistikOrderItem,
  LogistikWorkshopPackOrderItem,
  ProductSetOrderItem,
} from '@chancenwerk/db/LogistikOrder.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  ChancenBriefVariable,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkMaterial,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import {
  LogistikProductAvailability,
  LogistikShippingStatus,
  LogistikShippingStatusIcon,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import { colors } from '@chancenwerk/design/theme.js';
import { html } from 'lit';

export const g_getShippingStatusColor = (status: LogistikShippingStatus | LogistikOrderClearanceState.DECLINED) => {
  switch (status) {
    case LogistikShippingStatus.COMPLETED:
      return '#4CAF50';
    case LogistikShippingStatus.NEW:
      return '#2196F3';
    case LogistikShippingStatus.PROBLEM:
      return '#F44336';
    case LogistikShippingStatus.PROCESSING:
      return '#FFC107';
    case LogistikShippingStatus.FORWARDED:
    case LogistikShippingStatus.SHIPPED:
      return '#3F51B5';
    case LogistikOrderClearanceState.DECLINED:
      return '#de140d';
    default:
      return 'black';
  }
};

export const g_getShippingStatusIcon = (status: LogistikShippingStatus | LogistikOrderClearanceState.DECLINED) => {
  switch (status) {
    case LogistikShippingStatus.COMPLETED:
      return LogistikShippingStatusIcon.COMPLETED;
    case LogistikShippingStatus.NEW:
      return LogistikShippingStatusIcon.NEW;
    case LogistikShippingStatus.PROBLEM:
      return LogistikShippingStatusIcon.PROBLEM;
    case LogistikShippingStatus.PROCESSING:
      return LogistikShippingStatusIcon.PROCESSING;
    case LogistikShippingStatus.SHIPPED:
      return LogistikShippingStatusIcon.SHIPPED;
    case LogistikOrderClearanceState.DECLINED:
      return 'remove_circle';
    case LogistikShippingStatus.FORWARDED:
      return LogistikShippingStatusIcon.FORWARDED;
    default:
      return 'help';
  }
};

export const g_getAvailabilityStatusText = (availability: LogistikProductAvailability) => {
  return html` <cr-text ${css({ fontSize: '0.8rem', color: g_getAvailabilityStatusColor(availability) })}>
    ${availability}
  </cr-text>`;
};

export const g_getAvailabilityStatusColor = (availability: LogistikProductAvailability) => {
  switch (availability) {
    case LogistikProductAvailability.AVAILABLE:
      return colors.darkgreen;
    case LogistikProductAvailability.AVAILABLE_IN_1_TO_3_DAYS:
      return colors.chancen_orange_secondary;
    case LogistikProductAvailability.AVAILABLE_IN_7_DAYS:
      return colors.chancen_orange_secondary;
    case LogistikProductAvailability.AVAILABLE_IN_1_TO_2_WEEKS:
      return colors.chancen_orange_secondary;
    case LogistikProductAvailability.AVAILABLE_IN_OVER_6_WEEKS:
      return colors.chancen_orange_secondary;
    case LogistikProductAvailability.UNAVAILABLE:
      return colors.red;
    default:
      return colors.red;
  }
};

export const g_getProductCategoryString = (category: string) => {
  switch (category) {
    case LearningMaterial._class:
    case 'LearningMaterial':
      return 'CHANCENheft';
    case AdvertisingMaterial._class:
    case 'AdvertisingMaterial':
      return 'Werbe- und Akquisematerial';
    case WorkMaterial._class:
    case 'WorkMaterial':
      return 'Büromaterial';
    case EventTicket._class:
    case 'EventTicket':
      return 'Eintrittskarten';
    case Furnishing._class:
    case 'Furnishing':
      return 'Mobiliar';
    case WorkshopPack._class:
      return 'CHANCENakademie Workshop-Pakete';
    case ChancenBrief._class:
      return 'CHANCENbrief';
    case LogistikProductSet._class:
      return 'CHANCENset';
    case TravelCertificate._class:
      return 'CHANCENreise-Zertifikat';
    default:
      return 'unbekannt';
  }
};

export function reconstructCartItem(cartItem: LogistikCartItem, addedQuantity: number = 0) {
  const productCategory = cartItem.productCategory;
  const ret =
    productCategory === WorkshopPack._class
      ? new LogistikWorkshoppackCartiItem({
          quantity: cartItem.quantity + addedQuantity,
          productID: cartItem.productID,
          _id: cartItem._id,
          product: cartItem.product,
          productCategory: cartItem.productCategory,
          flyaVariableValues: cartItem.flyaVariableValues,
          trainersQuantity: (cartItem as LogistikWorkshoppackCartiItem).trainersQuantity,
          participantsQuantity: (cartItem as LogistikWorkshoppackCartiItem).participantsQuantity,
        })
      : productCategory === LearningMaterial._class
      ? new LogistikLearningMaterialCartItem({
          quantity: cartItem.quantity + addedQuantity,
          isDigital: (cartItem as LogistikLearningMaterialCartItem).isDigital,
          productID: cartItem.productID,
          _id: cartItem._id,
          product: cartItem.product,
          productCategory: cartItem.productCategory,
          flyaVariableValues: cartItem.flyaVariableValues,
        })
      : new LogistikCartItem({
          quantity: cartItem.quantity + addedQuantity,
          productID: cartItem.productID,
          _id: cartItem._id,
          product: cartItem.product,
          productCategory: cartItem.productCategory,
          flyaVariableValues: cartItem.flyaVariableValues,
        });
  return ret;
}

export function cloneLogistikOrderItem(orderItem: LogistikOrderItem) {
  const basic = {
    product: orderItem.product,
    productID: orderItem.productID,
    quantity: orderItem.quantity,
    productCategory: orderItem.productCategory,
  };
  switch (orderItem._class) {
    case LogistikWorkshopPackOrderItem._class:
      return new LogistikWorkshopPackOrderItem({
        ...basic,
        participantsQuantity: (orderItem as LogistikWorkshopPackOrderItem).participantsQuantity,
        train: (orderItem as LogistikWorkshopPackOrderItem).trainersQuantity,
      });
    case LogistikLearningMaterialOrderItem._class:
      return new LogistikLearningMaterialOrderItem({
        ...basic,
        isDigital: (orderItem as LogistikLearningMaterialOrderItem).isDigital,
      });
    case ProductSetOrderItem._class:
      return new ProductSetOrderItem(basic);
    case LogistikOrderChancenBriefItem._class:
      return new LogistikOrderChancenBriefItem({
        ...basic,
        isSelfPrint: (orderItem as LogistikOrderChancenBriefItem).isSelfPrint,
        flyaVariableValues: (orderItem as LogistikOrderChancenBriefItem).flyaVariableValues,
      });
    case LogistikOrderItem._class: // ! fallthrough
    default:
      return new LogistikOrderItem(basic);
  }
}

export function isCartItemValid(
  cartItem: LogistikCartItem,
  product:
    | LogistikProduct
    | NarrowedEntity<
        LogistikProduct,
        // @ts-expect-error flyaVariables only exists on some subclasses of LogistikProduct, we still want to list them here because they form the least amount of information from LogistikProduct we need
        { flyaVariables: { variableName: 1 } }
      >,
): boolean {
  if (product instanceof ChancenBrief || product instanceof TravelCertificate) {
    const variableExistsInOrderAndHasValue = (a: ChancenBriefVariable) =>
      !!cartItem?.flyaVariableValues?.some(b => b.variableName === a.variableName && b.variableValue);

    return !!product.flyaVariables?.length && product.flyaVariables.every(variableExistsInOrderAndHasValue);
  } else {
    return true;
  }
}

export function isEntireCartValid(
  cart: LogistikCart,
  products: Array<
    | LogistikProduct
    | NarrowedEntity<
        LogistikProduct,
        {
          _id: 1;
          flyaVariables: { variableName: 1 };
        }
      >
  >,
): boolean {
  return cart.items.every(item => {
    const product = products.find(p => p._id === item.productID);
    return !!product && isCartItemValid(item, product);
  });
}
